// Source of /sw.js. The build plugin (vite-plugin-service-worker.ts) transpiles this file alone,
// without bundling: it must not import anything at runtime.

export interface SwBuild {
  /** False emits a worker that removes itself and its caches (rollback of VITE_PWA). */
  enabled: boolean;
  version: string;
  /** Entry script of this build, e.g. `/assets/index-abc123.js`. */
  entry: string;
  /** Absolute paths cached at install, the shell excepted. */
  precache: string[];
}

export interface ExtendableEventLike {
  waitUntil(promise: Promise<unknown>): void;
}

export interface FetchEventLike extends ExtendableEventLike {
  request: Request;
  respondWith(response: Promise<Response> | Response): void;
}

export interface SwScope {
  location: { origin: string };
  caches: CacheStorage;
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  skipWaiting(): Promise<void>;
  clients: { claim(): Promise<void> };
  registration: { unregister(): Promise<boolean> };
  addEventListener(type: 'install' | 'activate', listener: (event: ExtendableEventLike) => void): void;
  addEventListener(type: 'fetch', listener: (event: FetchEventLike) => void): void;
}

export const CACHE_PREFIX = 'vsp-sw-';
export const SHELL_URL = '/';
// Same budget as the stale-chunk check: on a gym Wi-Fi without internet the request can
// hang for minutes, the cached shell is better than a blank page.
export const NAVIGATION_TIMEOUT_MS = 3_000;

const STATIC_FILE = /\.(?:svg|png|ico|webp|jpe?g|webmanifest|woff2?|ttf)$/i;
// Servers answer `Vary: Origin` and Vite's `<script crossorigin>` sends an Origin header
// that the install requests did not: without this every cached chunk misses offline.
const MATCH_ANY_VARY: CacheQueryOptions = { ignoreVary: true };

export type RequestRoute = 'navigation' | 'hashed-asset' | 'static-file' | 'passthrough';

export function routeRequest(request: Request, origin: string): RequestRoute {
  if (request.method !== 'GET') return 'passthrough';
  const url = new URL(request.url);
  // API responses belong to the IndexedDB read cache (VOL-14 lot 3), never to this worker.
  if (url.origin !== origin || url.pathname.startsWith('/api/')) return 'passthrough';
  if (request.mode === 'navigate') return 'navigation';
  if (url.pathname.startsWith('/assets/')) return 'hashed-asset';
  if (STATIC_FILE.test(url.pathname)) return 'static-file';
  // Includes chunkReload's `fetch('/')`: it must reach the server to see a new deploy.
  return 'passthrough';
}

/**
 * Keeps the current cache and the one created just before it: a tab still running the
 * previous build (until its next reload) keeps finding its lazy chunks offline.
 */
export function cachesToDelete(names: string[], current: string): string[] {
  const ours = names.filter((name) => name.startsWith(CACHE_PREFIX));
  const others = ours.filter((name) => name !== current);
  // CacheStorage.keys() lists caches in creation order.
  const previous = others[others.length - 1];
  return others.filter((name) => name !== previous);
}

const timeout = (ms: number): Promise<never> =>
  new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(`timed out after ${ms} ms`)), ms);
  });

export function installServiceWorker(scope: SwScope, build: SwBuild): void {
  const cacheName = `${CACHE_PREFIX}${build.version}`;

  if (!build.enabled) {
    scope.addEventListener('install', (event) => {
      event.waitUntil(scope.skipWaiting());
    });
    scope.addEventListener('activate', (event) => {
      event.waitUntil(
        (async () => {
          const names = await scope.caches.keys();
          await Promise.all(
            names.filter((name) => name.startsWith(CACHE_PREFIX)).map((name) => scope.caches.delete(name)),
          );
          await scope.registration.unregister();
        })(),
      );
    });
    return;
  }

  const precache = async (): Promise<void> => {
    const shell = await scope.fetch(SHELL_URL, { cache: 'reload' });
    if (!shell.ok) throw new Error(`shell: HTTP ${shell.status}`);
    // A deploy switching during the install would pair another build's page with these
    // chunks: fail, the browser retries at its next update check.
    if (!(await shell.clone().text()).includes(build.entry)) {
      throw new Error('shell does not belong to this build');
    }
    const cache = await scope.caches.open(cacheName);
    await cache.addAll(build.precache);
    await cache.put(SHELL_URL, shell);
  };

  scope.addEventListener('install', (event) => {
    event.waitUntil(precache().then(() => scope.skipWaiting()));
  });

  scope.addEventListener('activate', (event) => {
    event.waitUntil(
      (async () => {
        const stale = cachesToDelete(await scope.caches.keys(), cacheName);
        await Promise.all(stale.map((name) => scope.caches.delete(name)));
        await scope.clients.claim();
      })(),
    );
  });

  const putInCache = async (request: Request, response: Response): Promise<void> => {
    if (!response.ok || response.type !== 'basic') return;
    // A host rewriting unknown paths to index.html (200) would otherwise pin that page under
    // a chunk's name until the next deploy.
    if (response.headers.get('content-type')?.includes('text/html')) return;
    const cache = await scope.caches.open(cacheName);
    await cache.put(request, response);
  };

  // Network first: online, every reload gets the deployed index.html, so an old shell
  // never sticks. The cached shell only answers when the network fails or hangs.
  const handleNavigation = async (request: Request): Promise<Response> => {
    const network = scope.fetch(request);
    network.catch(() => undefined);
    try {
      return await Promise.race([network, timeout(NAVIGATION_TIMEOUT_MS)]);
    } catch {
      // Not caches.match(): it searches in creation order and would find the previous
      // build's shell first.
      const shell = await (await scope.caches.open(cacheName)).match(SHELL_URL);
      return shell ?? network;
    }
  };

  // Hashed file names never change content: cache first.
  const handleHashedAsset = async (request: Request): Promise<Response> => {
    const cached = await scope.caches.match(request, MATCH_ANY_VARY);
    if (cached) return cached;
    const response = await scope.fetch(request);
    await putInCache(request, response.clone()).catch(() => undefined);
    return response;
  };

  // Public files keep their name across deploys: answer from the cache, refresh behind.
  const handleStaticFile = async (event: FetchEventLike): Promise<Response> => {
    const cache = await scope.caches.open(cacheName);
    const cached =
      (await cache.match(event.request, MATCH_ANY_VARY)) ?? (await scope.caches.match(event.request, MATCH_ANY_VARY));
    const network = scope.fetch(event.request).then(async (response) => {
      await putInCache(event.request, response.clone()).catch(() => undefined);
      return response;
    });
    if (!cached) return network;
    event.waitUntil(network.catch(() => undefined));
    return cached;
  };

  scope.addEventListener('fetch', (event) => {
    const route = routeRequest(event.request, scope.location.origin);
    if (route === 'navigation') event.respondWith(handleNavigation(event.request));
    else if (route === 'hashed-asset') event.respondWith(handleHashedAsset(event.request));
    else if (route === 'static-file') event.respondWith(handleStaticFile(event));
  });
}

declare const __SW_BUILD__: SwBuild | undefined;

if (typeof __SW_BUILD__ !== 'undefined') {
  installServiceWorker(globalThis as unknown as SwScope, __SW_BUILD__);
}
